<template>
  <div
    :id="'dropDownSelect-' + componentKey"
    v-click-outside="closePopup"
    :class="[
      $style.wrap,
      $style[customClass],
      'vgp-interactive-element',
      {
        [$style.wrap_full_width]: fullWidth,
        [$style.active]: isHighlight,
        [$style.open]: isOpen && !disabled,
        [$style.disable]: disabled,
        [$style.readonly]: readonly,
        [$style.outline_none]: outline === 'none',
        [$style.outline_hover]: outline === 'hover',
        [$style.outline_always]: outline === 'always',
      },
    ]"
    :style="{
      'font-size': view.fontSize + 'px',
      'height': view.itemHeight ? view.itemHeight + 'px' : '32px'
    }"
  >
    <div
      :id="gpAutotestName"
      :data-testid="gpAutotestName || undefined"
      ref="selected_wrap"
      :class="$style.select_inner"
      @click="onClickDropTrigger"
    >
      <template v-if="useSearch && isOpen">
        <sprite
          :name="'search'"
          :type="'regular'"
          :size="24"
          :class="$style.search_icon"
        />
        <input
          :id="dropdownTestId('search') || ('select-drop-trigger-' + componentKey)"
          :data-testid="dropdownTestId('search')"
          ref="search_input"
          v-model="searchTxt"
          :class="$style.search_input"
          :placeholder="searchTxt ? '' : locale.search"
          :style="selectedInputWidthStyle"
          type="text"
        >
      </template>

      <sprite
        v-if="leftIcon"
        :name="leftIcon.name"
        :type="leftIcon.type"
        :size="leftIcon.size"
        :class="$style.left_icon"
      />

      <div
        :class="[$style.selected_items, useSearch && isOpen && $style.transparent]"
        :style="[selectedInputWidthStyle, leftIcon && { paddingLeft: `${leftIcon.size + 8}px` }]"
      >
        <span
          v-if="!localSelected.length"
          :class="$style.placeholder_txt"
        >
          {{ placeholder }}
        </span>

        <template v-if="multiselect">
          <template v-if="showStubChips">
            <vgpChip
              v-if="isSelectedAllItems && dropList.length"
              :gray-theme="true"
              :text="stubTextIfAllSelected"
              :preset="'text'"
            />
          </template>

          <template v-else>
            <vgpRollItems
              v-if="multiSelected.length"
              :key="componentKey + '-' + isOpen"
              :items="multiSelected"
              :offset="itemTemplate === 'user' ? -4 : 4"
            >
              <template #default="{item}">
                <vgpChip
                  :size="size"
                  :preset="multiChipPreset"
                  :text="item[resolvedFields.text]"
                  :img-url="item[resolvedFields.img]"
                  :bg-color="item[resolvedFields.bgColor]"
                  :txt-color="item[resolvedFields.textColor]"
                  :gray-theme="!['label', 'user', 'color'].includes(itemTemplate)"
                  :icon="item[resolvedFields.icon]"
                  :fill-width="itemTemplate === 'color' || itemTemplate === 'label' ? false : localSelected.length === 1"
                  :status-color="item[resolvedFields.statusColor]"
                  :need-description="needDescription"
                />
              </template>
            </vgpRollItems>
          </template>
        </template>

        <template v-if="!multiselect">
          <vgpChip
            v-if="singleSelected"
            :size="size"
            :preset="itemTemplate"
            :text="singleSelected[resolvedFields.text]"
            :img-url="singleSelected[resolvedFields.img]"
            :bg-color="singleSelected[resolvedFields.bgColor]"
            :txt-color="singleSelected[resolvedFields.textColor]"
            :icon="singleSelected[resolvedFields.icon]"
            :status-color="singleSelected[resolvedFields.statusColor]"
            :txt-ellipsis="true"
            :fill-width="itemTemplate !== 'label'"
          />
        </template>
      </div>

      <div
        ref="right_selected"
        :class="[$style.d_flex, $style.ai_center, $style.ml_8, $style.flex_shrink_0]"
      >
        <sprite
          v-show="isShowResetBtn"
          :name="'close-2'"
          :type="'bold'"
          :size="16"
          :class="[$style.mr_8, 'vgp_select_reset_btn']"
          @click="onClickResetBtn"
        />

        <span
          v-if="dropArrowIcon"
          :id="dropdownTestId('drop_arrow')"
          :data-testid="dropdownTestId('drop_arrow')"
          :class="[$style.drop_arrow, 'vgp_select_drop_arrow']"
          @click="toggleDropDown"
        >
          <sprite
            :name="dropArrowIcon.name"
            :type="dropArrowIcon.type"
            :size="dropArrowIcon.size"
          />
        </span>
      </div>
    </div>

    <div
      v-if="isOpen"
      ref="drop"
      v-scroll.self="onDropListScroll"
      :class="[$style.drop, 'gantt_scroll scroll_visible']"
      :style="{
        ...popupStyles,
        'max-height': popupMaxHeight + 'px',
      }"
    >
      <div>
        <div :style="{ 'height': '8px'}" />

        <template v-if="showItems.length">
          <div
            v-if="multiselect && useSelectAll"
            :id="dropdownTestId('select_all')"
            :data-testid="dropdownTestId('select_all')"
            :class="[$style.select_all_btn_wrap, !dropListInfo.isTop && $style.border_bottom]"
          >
            <vgp-button
              type="text-blue"
              :small="true"
              :has-margin="false"
              :label="isAllShowedItemsSelected ? locale.unselectAll : locale.selectAll"
              @onClick="onSelectAllBtnClick"
            />
          </div>

          <div
            v-for="groupName in groupSet"
            :key="componentKey + groupName"
            :style="{width: dropWidth +'px', }"
          >
            <div
              v-if="groupName"
              :class="$style.group_devider"
              :style="{ color: groupViewSettings.color }"
            >
              <span :class="$style.group_devider_name"> {{ groupName }} </span>
              <div
                v-if="groupViewSettings.divider"
                :class="$style.group_devider_line"
              />
            </div>

            <div
              v-for="item in itemsByGroup.get(groupName) ?? []"
              :key="item[resolvedFields.id]"
              v-tippy="tooltips(item) || undefined"
              :id="dropdownTestId('item_row_' + item[resolvedFields.id])"
              :class="[
                $style.drop_item,
                'js-drop-item',
                { [$style.disabled]: item.disabled },
              ]"
              :title="useSystemTooltip ? item[resolvedFields.text] : ''"
              :style="{ width: dropWidth + 'px' }"
              @click="onItemClick(item)"
            >
              <vgpDropListItem
                :active="isSingleSelectedItem(item)"
                :disabled="item.disabled"
                :size="size === 'autoWidth' ? 'sm' : size"
                :style="view.itemHeight ? { minHeight: view.itemHeight + 'px' } : {}"
              >
                <template
                  v-if="multiselect && !checkboxAfter"
                  #left
                >
                  <div style="pointer-events: none">
                    <vgpCheckbox
                      :checked="isMultiChecked(item)"
                      :is-disable="item.disabled"
                    />
                  </div>
                </template>

                <vgpChip
                  :text="item[resolvedFields.text]"
                  :description="item.description"
                  :need-description="needDescription"
                  :img-url="item[resolvedFields.img]"
                  :bg-color="item[resolvedFields.bgColor]"
                  :txt-color="item[resolvedFields.textColor]"
                  :status-color="item[resolvedFields.statusColor]"
                  :preset="itemTemplate"
                  :size="size"
                  :icon="item[resolvedFields.icon]"
                  :txt-ellipsis="true"
                  :selected="isSingleSelectedItem(item)"
                  :fill-width="itemTemplate !== 'label'"
                />

                <template #right>
                  <div
                    v-if="multiselect && checkboxAfter"
                    style="pointer-events: none"
                  >
                    <vgpCheckbox
                      :checked="isMultiChecked(item)"
                      :is-disable="item.disabled"
                    />
                  </div>
                  <vgpChip
                    v-if="resolvedFields.additional && item[resolvedFields.additional]"
                    :text="item[resolvedFields.additional].text"
                    :description="item.description"
                    :need-description="needDescription"
                    :txt-color="item[resolvedFields.additional].txtColor"
                    :img-url="additionalItemImgUrl(item[resolvedFields.additional])"
                    :bg-color="item[resolvedFields.additional].bgColor"
                    :status-color="item[resolvedFields.additional].statusColor"
                    :preset="item[resolvedFields.additional].preset"
                    :round="item[resolvedFields.additional].round"
                    :padding-left="item[resolvedFields.additional].paddingLeft"
                    :padding-right="item[resolvedFields.additional].paddingRight"
                    :gray-theme="item[resolvedFields.additional].grayTheme"
                    :selected="isSingleSelectedItem(item)"
                    :size="size"
                    :icon="item[resolvedFields.additional].iconName"
                    :txt-ellipsis="true"
                    :fill-width="itemTemplate !== 'label'"
                  />
                  <div
                    v-if="item.iconButtons"
                    :class="$style.icon_buttons"
                    @click.stop
                  >
                    <icon-button
                      v-for="(button, index) in item.iconButtons"
                      :key="index"
                      v-bind="iconButtonBindings(button, item)"
                      :size="button.size"
                      @onClick="button.handler(item)"
                    />
                  </div>
                </template>
              </vgpDropListItem>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            :class="$style.no_search"
            :style="{width: popupStyles.width}"
          >
            <span> {{ noMatchesMessage }} </span>
          </div>
        </template>

        <div :style="{'height': '8px'}" />
      </div>
    </div>
  </div>
</template>

<script>
import vgpChip from '$$vueCmp/chip/vgpChip.vue';
import vgpRollItems from '$$uiKitComp/rollItems/vgpRollItems.vue';
import sprite from '$$uiKitComp/VueIcon/svgSprite.vue';
import iconButton from '$$uiKitComp/globalButton/withIcon/iconButton.vue';
import vgpButton from '$$uiKitComp/globalButton/vgpButton.vue';
import vgpDropListItem from './vgpDropListItem.vue';
import { useDropdownPosition } from './useDropdownPosition';
import { useDropdownItems } from './useDropdownItems';
import vgpCheckbox from '$$uiKitComp/checkbox/vgpCheckbox.vue';

const groupDefaultSettings = {
  divider: true,
  color: '#999999',
};

const SCROLL_CONTAINER_SELECTORS = [
  'div.gantt_scroll',
  'div.gantt_scroll .v-table__wrapper',
  'div.vgp_scroll_bar_container .simplebar-content-wrapper',
];

export default {
  name: 'VgpDropdownSelect',
  components: {
    vgpChip, vgpRollItems, sprite, iconButton, vgpButton, vgpDropListItem, vgpCheckbox,
  },

  setup() {
    const { popupStyles, dropWidth, calcPopupPosition } = useDropdownPosition();
    const { showItems, updateBySearch, sortBySelected } = useDropdownItems();

    return {
      popupStyles, dropWidth, calcPopupPosition, showItems, updateBySearch, sortBySelected,
    };
  },

  props: {
    gpAutotestName: { type: String, default: '' },
    dropList: { type: Array, default: () => [] },
    selected: { type: [Array, Object], default: () => [] },
    multiselect: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Placeholder' },
    groupBy: { type: String, default: '' },
    useSearch: { type: Boolean, default: false },
    useSelectAll: { type: Boolean, default: false },
    showSelectedFirst: { type: Boolean, default: true },
    popupMaxHeight: { type: Number, default: 240 },
    size: {
      type: String,
      default: 'sm',
      validator(value) {
        return ['sm', 'lg', 'autoWidth'].includes(value);
      },
    },
    itemTemplate: {
      type: String,
      default: 'text',
      validator(value) {
        return ['text', 'user', 'label', 'withImg', 'withIcon', 'status', 'color'].includes(value);
      },
    },
    showResetBtn: { type: Boolean, default: true },
    /** When true, reset button is hidden for single select when dropdown is closed (e.g. in reports filter) */
    hideResetWhenSingleClosed: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    highlightActiveState: { type: Boolean, default: false },
    outline: {
      type: String,
      default: 'always',
      validator(value) {
        return ['none', 'always', 'hover'].includes(value);
      },
    },
    /** Items that are mutually exclusive with all others: selecting one clears the rest, and vice versa. */
    exclusiveItems: { type: Array, default() { return []; } },
    dropPosition: {
      type: String,
      default: 'absolute',
      validator(value) {
        return ['fixed', 'absolute'].includes(value);
      },
    },
    tooltipPosition: { type: String, default: 'left' },
    parentSelectorForCalcPositionDrop: { type: String, default: '' },
    /**
     * Keys: id, text, icon, img, bgColor, statusColor, textColor, additional, tooltip.
     * Only pass keys that differ from the defaults.
     * @example :field-map="{ text: 'fullName', img: 'photo' }"
     */
    fieldMap: { type: Object, default: () => ({}) },

    stubTextIfAllSelected: { type: String, default: '' },
    useSystemTooltip: { type: Boolean, default: false },
    noMatchesMessage: { type: String, default: __('no_matches_message_text') },
    fixedWidth: { type: Number, default: 0 },
    /**
     * Stretch the control to 100% of the parent width (trigger + drop list width match trigger unless fixedWidth is set).
     */
    fullWidth: { type: Boolean, default: false },
    needDescription: { type: Boolean, default: false },

    groupSettings: { type: Object, default: () => {} },
    leftIcon: { type: Object, default: null },
    arrowIcon: { type: Object, default: null },
    customClass: { type: String, default: '' },
    checkboxAfter: { type: Boolean, default: false },
  },

  emits: {
    selected: [], // items[], emit after popup close if has changes selected item
    onChange: [], // items[], emit after every selected/unselected item
    onResetClick: null,
    onOpen: null,
    onClose: null,
  },

  data() {
    return {
      componentKey: `drop_select_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      locale: {
        search: __('filter_search_placeholder'),
        selectAll: __('multicombo_select_all_items'),
        unselectAll: __('multicombo_unselect_all_items'),
      },
      isOpen: false,
      searchTxt: '',
      dropListInfo: {
        isTop: true,
      },
      localSelected: [],
      scrollContainerElements: null,
    };
  },

  computed: {
    resolvedFields() {
      return {
        id: 'id',
        text: 'name',
        icon: 'icon',
        img: 'img',
        bgColor: 'color',
        statusColor: 'color',
        textColor: 'textColor',
        additional: '',
        tooltip: '',
        ...this.fieldMap,
      };
    },

    showStubChips() {
      return this.isSelectedAllItems && !!this.stubTextIfAllSelected;
    },

    multiChipPreset() {
      return this.itemTemplate === 'user' && this.localSelected.length > 1
        ? 'avatar'
        : this.itemTemplate;
    },

    selectedArray() {
      if (Array.isArray(this.selected)) {
        return [...this.selected];
      }

      if (this.selected) {
        return [this.selected];
      }

      return [];
    },

    multiSelected: {
      get() {
        return this.localSelected;
      },

      set(newSelected) {
        const preparedNewSelected = this.prepareNewMultiSelected(newSelected);

        this.localSelected = preparedNewSelected;
        this.emitChange(preparedNewSelected);
      },
    },

    singleSelected: {
      get() {
        return this.localSelected[0] || '';
      },

      set(newVal) {
        this.localSelected = [newVal];
        this.emitChange([newVal]);
        this.closePopup();
      },
    },

    selectedInputWidthStyle() {
      if (this.stubTextIfAllSelected && this.isAllShowedItemsSelected) return;

      // 24px icon + 8px ml_8 margin on the right wrapper = 32px reserved for the arrow area
      let delta = 32;

      if (this.isShowResetBtn) delta += 40;
      if (this.useSearch && this.isOpen) delta += 38;

      return { width: `calc(100% - ${delta}px)` };
    },

    view() {
      if (this.size === 'lg') return { fontSize: 16, itemHeight: 40 };
      if (this.size === 'autoWidth') return { fontSize: 14 };

      return { fontSize: 14, itemHeight: 32 };
    },

    isHighlight() {
      const highlightActive = this.highlightActiveState && (this.searchTxt || this.localSelected.length);

      return !this.disabled && (this.isOpen || highlightActive);
    },

    isShowResetBtn() {
      const length = this.localSelected.length;

      if ((length === this.dropList.length) && this.stubTextIfAllSelected) return false;
      if (this.disabled) return false;

      const hasResetContent = this.useSearch && this.isOpen ? !!this.searchTxt : !!length;
      const singleClosedOk = !this.multiselect && !this.isOpen ? !this.hideResetWhenSingleClosed : true;

      return this.showResetBtn && hasResetContent && singleClosedOk;
    },

    groupSet() {
      const seen = new Set();
      const groups = [];

      for (const item of this.showItems) {
        const groupName = (this.groupBy in item) ? item[this.groupBy] : null;

        if (!seen.has(groupName)) {
          seen.add(groupName);
          groupName ? groups.push(groupName) : groups.unshift(groupName);
        }
      }

      return groups;
    },

    selectedIds() {
      const idField = this.resolvedFields.id;

      return new Set(this.localSelected.map(i => i[idField]));
    },

    itemsByGroup() {
      const map = new Map();

      for (const item of this.showItems) {
        const key = (this.groupBy in item) ? item[this.groupBy] : null;

        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
      }

      return map;
    },

    isAllShowedItemsSelected() {
      if (this.localSelected.length < this.showItems.length) return false;

      const idField = this.resolvedFields.id;

      return this.showItems.every(item => this.selectedIds.has(item[idField]));
    },

    isSelectedAllItems() {
      return this.localSelected.length === this.dropList.length;
    },

    groupViewSettings() {
      return {
        ...groupDefaultSettings,
        ...this.groupSettings,
      };
    },

    dropArrowIcon() {
      return this.arrowIcon || { name: 'drop-down', type: 'bold', size: 24 };
    },
  },

  watch: {
    selectedArray: {
      handler(val) {
        this.localSelected = val;
      },
      immediate: true,
    },
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          this.sortShowedItemsBySelected();
          this.$refs.search_input && this.$refs.search_input.focus();
          this.$emit('onOpen');
          requestAnimationFrame(() => this.checkPopupPosition());
        });
      } else {
        this.searchTxt = '';
        this.$emit('onClose');
      }
    },

    searchTxt: 'updateShowedItemsBySearch',

    showItems() {
      if (this.isOpen) {
        this.$nextTick(() => requestAnimationFrame(() => this.checkPopupPosition()));
      }
    },

    dropList(data) {
      if (data.length) {
        const localSelected = data.filter(item => this.selectedIds.has(item[this.resolvedFields.id]));

        if (this.localSelected.length !== localSelected.length) {
          this.emitChange(localSelected);
          this.emitSelected(localSelected);
        }

        this.localSelected = localSelected;
      }
      this.updateShowedItemsBySearch();
    },
  },

  beforeMount() {
    this.updateBySearch(this.dropList, '', this.resolvedFields);
  },

  mounted() {
    this.scrollContainerElements = this.getScrollContainerElements();
    this.scrollContainerElements.forEach(el => el.addEventListener('scroll', this.onScroll));
    document.body.addEventListener('keydown', this.onKeydown);

    if (this.fixedWidth) {
      this.dropWidth = this.fixedWidth;
    }
  },

  beforeUnmount() {
    // this._scrollElements?.forEach(el => el.removeEventListener('scroll', this.onScroll));
    document.body.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    dropdownTestId(suffix) {
      if (!this.gpAutotestName) {
        return undefined;
      }

      return suffix ? `${this.gpAutotestName}_${suffix}` : this.gpAutotestName;
    },

    emitChange(val) {
      this.$emit('onChange', [...val]);
    },
    emitSelected(val) {
      this.$emit('selected', [...val]);
    },
    prepareNewMultiSelected(newSelected) {
      if (!this.exclusiveItems.length) {
        return newSelected;
      }

      const idField = this.resolvedFields.id;
      const exclusiveIds = new Set(this.exclusiveItems.map(i => i[idField]));
      const exclusiveAlreadySelected = this.localSelected.find(item => exclusiveIds.has(item[idField]));
      const exclusiveInNewSelected = newSelected.some(item => exclusiveIds.has(item[idField]));

      if (exclusiveInNewSelected && !exclusiveAlreadySelected) {
        return [...this.exclusiveItems];
      }

      if (exclusiveAlreadySelected) {
        return newSelected.filter(item => item[idField] !== exclusiveAlreadySelected[idField]);
      }

      return newSelected;
    },
    tooltips(item) {
      const tooltip = item[this.resolvedFields.tooltip];

      if (!tooltip?.content) return;

      return {
        placement: tooltip.placement || 'left',
        interactive: tooltip.interactive || false,
        content: tooltip.content,
        arrow: true,
        delay: 0,
        duration: [0, 0],
        theme: tooltip.theme || '',
        maxWidth: 250,
      };
    },

    isSingleSelectedItem(item) {
      const idField = this.resolvedFields.id;

      return !this.multiselect && !!this.singleSelected && this.singleSelected[idField] === item[idField];
    },

    isMultiChecked(item) {
      return this.selectedIds.has(item[this.resolvedFields.id]);
    },

    onItemClick(item) {
      if (item.disabled) return;

      if (this.multiselect) {
        const idField = this.resolvedFields.id;
        const isChecked = this.isMultiChecked(item);
        const newSelected = isChecked
          ? this.localSelected.filter(s => s[idField] !== item[idField])
          : [...this.localSelected, item];

        this.multiSelected = newSelected;
      } else {
        this.singleSelected = item;
      }
    },

    additionalItemImgUrl(additional) {
      if (!additional) return undefined;

      return additional.imgUrl ?? additional['img-url'] ?? undefined;
    },

    iconButtonBindings(button, item) {
      const isSelected = this.isSingleSelectedItem(item);
      const base = {
        class: this.$style.icon_btn,
        icon: button.icon,
      };

      if (!isSelected) return base;

      /* iconButton sprite ignores icon.color; --color on .simple is grey. Use custom colors so SVG inherits white. */
      return {
        ...base,
        colors: {
          color: '#fff',
          labelColor: '#fff',
          activeColor: '#fff',
          bg: 'transparent',
          hoverBg: 'rgba(255, 255, 255, 0.24)',
          activeBg: 'rgba(255, 255, 255, 0.2)',
          activeHoverBg: 'rgba(255, 255, 255, 0.3)',
        },
      };
    },

    closePopup() {
      if (!this.isOpen) return;

      this.emitSelected([...this.localSelected]);
      this.isOpen = false;
    },

    getScrollContainerElements() {
      return SCROLL_CONTAINER_SELECTORS.flatMap(sel => [...document.querySelectorAll(sel)]);
    },

    onScroll(e) {
      if (!this.isOpen) return;

      if (!this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },

    onClickDropTrigger(e) {
      const path = e.composedPath();

      if (!this.$refs.selected_wrap) {
        return;
      }

      const isControlElement = path.some(elem => elem.classList?.contains('vgp_select_drop_arrow') || elem.classList?.contains('vgp_select_reset_btn'));

      if (isControlElement) return;

      this.toggleDropDown();
    },

    onClickResetBtn() {
      if (this.isOpen && this.useSearch) {
        this.searchTxt = '';
        this.$refs.search_input && this.$refs.search_input.focus();

        return;
      }

      this.$emit('onResetClick');
      this.emitChange([]);
      this.emitSelected([]);
      this.localSelected = [];
    },

    sortShowedItemsBySelected() {
      if (!this.showSelectedFirst) return;

      this.sortBySelected(this.groupSet, this.itemsByGroup, this.selectedIds, this.resolvedFields.id);
    },

    updateShowedItemsBySearch() {
      this.updateBySearch(this.dropList, this.searchTxt, this.resolvedFields);
    },

    onSelectAllBtnClick() {
      const idField = this.resolvedFields.id;
      const showItemIds = new Set(this.showItems.map(i => i[idField]));

      if (this.isAllShowedItemsSelected) {
        this.multiSelected = this.localSelected.filter(item => !showItemIds.has(item[idField]));
      } else {
        this.multiSelected = [...this.showItems];
      }

      this.emitSelected(this.localSelected);
    },

    onDropListScroll(e) {
      this.dropListInfo.isTop = this.$refs.drop.scrollTop === 0;

      const el = this.resolvedFields.tooltip && document.querySelector('[data-tippy-root]');

      if (el && el._tippy && el._tippy.state?.isVisible) {
        el._tippy.hide();
      }
    },

    toggleDropDown() {
      this.isOpen ? this.closePopup() : this.openPopup();
    },

    openPopup() {
      this.updateShowedItemsBySearch();
      this.isOpen = true;
    },

    checkPopupPosition() {
      this.calcPopupPosition(this.$refs.selected_wrap, this.$refs.drop, {
        mode: this.dropPosition,
        parentSelector: this.parentSelectorForCalcPositionDrop,
        fixedWidth: this.fixedWidth,
      });
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
    },
  },
};
</script>

<style module lang="less" src="./dropSelect.less"></style>
